import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Chip, Dialog as MuiDialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography} from '@mui/material';
import Button from '../../../../../../../components/base/button/button';
import useIsMounted from '../../../../../../../hooks/use-is-mounted';
import {DashboardEntry, DashboardGroupEntity} from '../../types';
import Api from "../../../../../../../../core/services/api/api";
import LoadingLottieAnimation from "../../../../../../../components/app-specific/lottie-animations/loading";

interface ShareDashboardDialogProps {
    open: boolean;
    dashboard: DashboardEntry | null;
    onClose: (groupIds?: DashboardGroupEntity['id'][]) => void;
}

const ShareDashboardDialog = ({open, dashboard, onClose}: ShareDashboardDialogProps) => {
    const [loading, setLoading] = useState(false);
    const [availableGroups, setAvailableGroups] = useState<DashboardGroupEntity[]>([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState<DashboardGroupEntity['id'][]>([]);
    const [applying, setApplying] = useState(false);
    const isMounted = useIsMounted();

    const selectedGroups = useMemo(() => selectedGroupIds.map(id => availableGroups.find(group => group.id === id)).filter(Boolean) as DashboardGroupEntity[], [availableGroups, selectedGroupIds]);
    const unselectedGroups = useMemo(() => availableGroups.filter(group => !selectedGroupIds.includes(group.id)), [availableGroups, selectedGroupIds]);

    /**
     * Fetches all available groups from the server
     */
    const getAvailableGroups = useCallback(async () => {
        setLoading(true);
        const response = await Api.getAllOperatorGroups();
        if (!isMounted()) return;
        setLoading(false);
        if (response?.resultFlag) {
            setAvailableGroups(response.data ?? []);
        }
    }, [isMounted]);

    /**
     * Initialize selected groups from dashboard when dialog opens
     */
    useEffect(() => {
        if (open && dashboard) {
            setApplying(false);
            setSelectedGroupIds(dashboard?.groupIds ?? []);
            void getAvailableGroups();
        }
    }, [open, dashboard, getAvailableGroups]);

    /**
     * Handles adding a new group to the selected groups
     */
    const handleGroupAdd = useCallback((group: DashboardGroupEntity) => {
        setSelectedGroupIds(prevGroups => {
            if (prevGroups.some(g => g === group.id)) {
                return prevGroups;
            }
            return [...prevGroups, group.id];
        });
    }, []);

    /**
     * Handles removing a group from the selected groups
     */
    const handleGroupRemove = useCallback((groupId: DashboardGroupEntity['id']) => {
        setSelectedGroupIds(prevGroups => prevGroups.filter(group => group !== groupId));
    }, []);

    /**
     * Handles applying the changes and closing the dialog
     */
    const handleApply = useCallback(async () => {
        setApplying(true);
        const response = await Api.setCustomDashboardGroups({
            dashboardId: dashboard?.id,
            groupIds: selectedGroupIds,
        })
        if (!isMounted())
            return;
        setApplying(false);
        if (response?.resultFlag) {
            onClose(selectedGroupIds);
        }
    }, [dashboard?.id, selectedGroupIds, isMounted, onClose]);

    /**
     * Handles closing the dialog without saving changes
     */
    const handleClose = useCallback(() => {
        onClose(dashboard?.groupIds ?? []);
    }, [dashboard?.groupIds, onClose]);

    return (
        <MuiDialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Share Dashboard</DialogTitle>
            <DialogContent>
                {
                    loading &&
                    <Box display="flex" justifyContent="center" p={3}>
                        <LoadingLottieAnimation width={'80%'}/>
                    </Box>
                }
                {
                    !loading &&
                    <>
                        <Typography variant="body2" gutterBottom>
                            Please select the groups you want to share this dashboard with.
                            Note that the dashboard will be visible to all users in the selected groups and if no groups are selected, the dashboard will be private to you only.
                        </Typography>
                        <Box marginBlock={3}>
                            <TextField
                                fullWidth
                                select
                                disabled={unselectedGroups.length === 0 || applying}
                                value={null}
                                label={"New group"}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleGroupAdd(unselectedGroups.find(group => group.id === parseInt(e.target.value))!);
                                    }
                                }}
                            >
                                {
                                    unselectedGroups.map((group) => (
                                        <MenuItem key={group.id} value={group.id.toString()}>
                                            {group.name}
                                        </MenuItem>
                                    ))
                                }
                            </TextField>
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {
                                selectedGroups.map((group) => (
                                    <Chip
                                        key={group.id}
                                        label={group.name}
                                        onDelete={() => handleGroupRemove(group.id)}
                                    />
                                ))
                            }
                            {
                                selectedGroupIds.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" textAlign={'center'} marginBlock={2} width={'100%'}>
                                        No groups selected
                                    </Typography>
                                )
                            }
                        </Box>
                    </>
                }
            </DialogContent>
            <DialogActions className={'d-flex align-items-center justify-content-between'} sx={{padding: 0}}>
                <Button
                    disabled={!open || applying}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={handleClose}
                    borderRadius={'0 8px 0 8px'}
                >
                    Close
                </Button>
                <Button
                    disabled={!open || applying}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={handleApply}
                    borderRadius={'8px 0 8px 0'}
                >
                    {
                        applying
                            ? "Processing..."
                            : "Apply"
                    }
                </Button>
            </DialogActions>
        </MuiDialog>
    );
};

export default ShareDashboardDialog;